/* eslint-disable */
import { ApolloClient } from '@apollo/client';
import React, { useEffect, useState, PureComponent } from 'react';
import { TFunction } from 'i18next';
import { pathOr, isNil, equals } from 'ramda';
import { Form as ReactstrapForm } from 'reactstrap';
import { Button, Col, Row, Infobox } from '@zoom/components';
import { Form, FormRenderProps, Field, FormSpy } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { withRouter, RouteComponentProps, Link } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
import { differenceInCalendarDays, isAfter, isBefore, isEqual, isWithinInterval, startOfDay } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { FormState, FormApi } from 'final-form';
import { ArrowRightIcon } from '@eleveo/icons-react';

import { DatePickerField } from '../../components/DatePicker';
import { SelectField } from '../../components/Inputs/SelectField';
import { finalFormInputHelper } from '../../components/Inputs/RenderInput';
import { Content } from '../../components/layout/Content';
import { required, max255chars } from '../../utils/validations';
import { createEndOfDateRange, convertFieldsEndOfDateRange } from '../../utils/dateRange';
import { composeValidators } from '../../utils/validations/composeValidators';
import { withinRange } from '../../utils/validations/withinRange';
import { multipleOptionsRange } from '../../utils/validations/multipleOptionsRange';
import {
  UpdateScheduleInfoMutationFn,
  UpdateScheduleMutationFn,
  CreateScheduleMutationFn,
  GetSchedulesDocument,
  CreateScheduleMutationVariables,
  ScheduledAgentInput,
  Queue,
  ScheduleInfo,
  GetScheduleDocument,
  GetScheduleQuery,
  ScheduleItemInput,
  ScheduleDaySettingsInput,
  StateEnum,
} from '../../graphql/components';
import { PATH_SCHEDULING_STUDIO } from '../../router/routes';
import {
  CREATE_TITLE_TAG,
  getDateRangeInputTag,
  getButtonTestId,
  SAVE_BUTTON,
  NEXT_STEP_BUTTON,
  CANCEL_BUTTON,
} from './constants';
import {
  InactiveAgentConfirmationModal,
  InactiveAgentModalState,
} from './SchedulingComposer/InactiveAgentConfirmationModal';
import { DateRangeChangeModal, DateRangeChangeModalStatus } from './DateRangeChangeModal';
import { CreateEditView, ActionRouteType, IdRouteType } from '../../types';
import { QueuesResult, SchedulesResult } from '../../graphql/types';
import { defaultGetSchedulesVariables, MAX_COUNT_OF_SELECTED_QUEUES } from './config';
import { PromptModal } from '../../components/Prompt/PromptModal';
import { cloneSchedule, shiftItemsDateRange } from './utils/transformScheduleItems';
import { getComposerPathname, getPathname } from './utils/pathname';
import { getDefaultDaySettings } from './utils/formatUtils';
import { ScheduleState, FormValues, LocationState } from './types';
import { nameOf } from '../../utils/helpers';
import { inRange } from '../../utils/validations/inRange';
import { isInteger } from '../../utils/validations/number';
import { recordBy } from '../../utils/array';
import { chooseTimezoneFromSelectedQueues } from '../../utils/timezone';
import { getUTCHoursOffsetLabel } from '../../utils/date';
import { getPrevalentOffsetDateFromInterval } from '../../utils/timezone';
import { TimezoneName } from '../../components/TimezoneName';
import { SchedulesStates } from '../../app/BackgroundTasksStatusProvider';
import { useAuth } from '../../app/AuthorizationProvider';
import { Separator } from '../../components/layout/Separator';

interface CreateScheduleSceneState {
  isEdit: boolean;
  isView: boolean;
  isCreate: boolean;
  inactiveAgentModal: InactiveAgentModalState;
  dateRangeChangeModalStatus: DateRangeChangeModalStatus;
  scheduleState: ScheduleState;
  oldFormValues?: FormValues;
  createdScheduleId?: string;
}

export type CreateScheduleSceneProps = {
  schedulesStates: SchedulesStates;
  createSchedule: CreateScheduleMutationFn;
  updateSchedule: UpdateScheduleMutationFn;
  updateScheduleInfo: UpdateScheduleInfoMutationFn;
  queuesResult: QueuesResult;
  schedulesResult: SchedulesResult;
  initialScheduleInfo?: ScheduleInfo;
  client: ApolloClient<{}>;
} & RouteComponentProps<ActionRouteType & Partial<IdRouteType>, {}, LocationState> &
  WithTranslation;

/**
 * Schedule form body elements.
 */
const scheduleNameField = (t: TFunction, disabled: boolean) =>
  finalFormInputHelper({
    disabled,
    label: t('schedule.name'),
    dataTestId: getDateRangeInputTag('name'),
    type: 'text',
  });

const startDateField = (t: TFunction, props: FormRenderProps<FormValues>, disabled: boolean) =>
  DatePickerField({
    disabled,
    dateFormat: t('date.formats.default'),
    label: t('date.dateFrom'),
    startDate: props.values.schedulingStartDateTime || undefined,
    endDate: props.values.schedulingEndDateTime || undefined,
    minDate: startOfDay(new Date()),
    selectsStart: true,
    dataTestId: getDateRangeInputTag('start-date'),
  });

const endDateField = (t: TFunction, props: FormRenderProps<FormValues>, disabled: boolean) =>
  DatePickerField({
    disabled,
    dateFormat: t('date.formats.default'),
    label: t('date.dateTo'),
    startDate: props.values.schedulingStartDateTime || undefined,
    endDate: props.values.schedulingEndDateTime || undefined,
    minDate: props.values.schedulingStartDateTime || startOfDay(new Date()),
    selectsEnd: true,
    dataTestId: getDateRangeInputTag('end-date'),
  });

const availableSeatsField = (t: TFunction, disabled: boolean) =>
  finalFormInputHelper({
    disabled,
    label: t('schedule.numberOfSeats'),
    dataTestId: getDateRangeInputTag('seats'),
  });

/**
 * Schedule form body.
 */
export interface CreateScheduleBodyProps {
  queuesResult: QueuesResult;
  schedulesResult: SchedulesResult;
  t: TFunction;
  props: FormRenderProps<FormValues>;
  isEdit: boolean;
  isView: boolean;
  isCreate: boolean;
  scheduleState?: StateEnum;
}

export const CreateScheduleBody: React.FC<CreateScheduleBodyProps> = ({
  queuesResult,
  schedulesResult,
  t,
  props,
  isEdit,
  isView,
  isCreate,
  scheduleState,
}) => {
  const allSchedules: ScheduleInfo[] = pathOr([], ['data', 'schedules'], schedulesResult);
  const allQueues: Queue[] = pathOr([], ['data', 'queues'], queuesResult);
  const allQueuesRecordObj = recordBy(allQueues, 'id');

  const [schedules, setSchedules] = useState<ScheduleInfo[]>([]);

  const { locale } = useAuth();

  const { schedulingStartDateTime, schedulingEndDateTime, cloneScheduleId, timezone } = props.values;
  const disabled = isEdit || isView;
  const disabledAvailableSeats = isView || scheduleState === StateEnum.Production;

  useEffect(() => {
    setSchedules(allSchedules);
  }, [schedulesResult]);

  const timezoneDate =
    schedulingEndDateTime && schedulingStartDateTime && timezone && schedulingStartDateTime <= schedulingEndDateTime
      ? getPrevalentOffsetDateFromInterval(schedulingStartDateTime, schedulingEndDateTime, timezone)
      : null;

  return (
    <div className="form-column-l scheduling-form">
      <OnChange name="cloneScheduleId">
        {(value, previous) => {
          // Select queues based on selected schedule to clone
          if (!!value && value !== previous) {
            const newQueueIds = allSchedules.find((schedule) => schedule.id === value)!.queueIds;
            props.form.change('queueIds', newQueueIds);
          }
        }}
      </OnChange>

      <OnChange name="queueIds">
        {(value, previous) => {
          if (value !== previous) {
            // Reset selected schedule to clone when its queue does not match selected queues
            if (!!cloneScheduleId) {
              const cloneToScheduleQueueIds = allSchedules.find(
                (schedule) => schedule.id === cloneScheduleId
              )!.queueIds;

              if (!equals(value, cloneToScheduleQueueIds)) {
                props.form.change('cloneScheduleId', undefined);
              }
            }

            const newTimeZone = chooseTimezoneFromSelectedQueues(allQueuesRecordObj, value);

            if (props.values.timezone !== newTimeZone) {
              props.form.change('timezone', newTimeZone);
            }
          }
        }}
      </OnChange>
      <Row>
        <Col xs="12">
          <Field
            name="name"
            validate={composeValidators(required, max255chars)}
            render={scheduleNameField(t, isView)}
          />
        </Col>
      </Row>
      {isCreate && (
        <Row className="mt-2">
          <Col xs="12">
            <SelectField
              data-test-id="schedules-dropdown"
              t={t}
              name="cloneScheduleId"
              label="schedule.createFromTemplate"
              disabled={disabled || schedules.length === 0}
              options={schedules}
              className="w-100"
            />
          </Col>
        </Row>
      )}
      <Row className="mt-2">
        <Col xs="12">
          <SelectField
            disabled={disabled}
            multiple
            label="queue.queue"
            name="queueIds"
            options={allQueues ?? []}
            t={t}
            validate={
              disabled ? undefined : composeValidators(required, multipleOptionsRange(1, MAX_COUNT_OF_SELECTED_QUEUES))
            }
            className="w-100"
          />
        </Col>
      </Row>
      {timezone && (
        <>
          <Row className="mt-4">
            <h2>{t('timezone.scheduleTimezone')}</h2>
          </Row>
          <Row className="schedule-timezone">
            <Col data-test-id="schedule-default-timezone-name" sm="auto" className="schedule-timezone-name my-0">
              <TimezoneName timezone={timezone} />
            </Col>
            {timezoneDate && (
              <Col
                data-test-id="schedule-default-timezone-offset-to-UTC"
                sm="auto"
                className="schedule-timezone-offset ml-auto my-0"
              >
                {getUTCHoursOffsetLabel(timezoneDate, timezone, locale, t)}
              </Col>
            )}
          </Row>
        </>
      )}
      <Row className="mt-4">
        <h2>{t('schedule.scheduleRange')}</h2>
      </Row>
      <Row>
        <Col xs="6">
          <Field
            name="schedulingStartDateTime"
            validate={
              disabled
                ? undefined
                : composeValidators(required, withinRange(startOfDay(new Date()), schedulingEndDateTime!))
            }
            key={schedulingEndDateTime ? schedulingEndDateTime.toISOString() : undefined}
            component={startDateField(t, props, disabled)}
          />
        </Col>
        <Col xs="6">
          <Field
            name="schedulingEndDateTime"
            validate={
              disabled
                ? undefined
                : composeValidators(required, withinRange(schedulingStartDateTime || startOfDay(new Date()), undefined))
            }
            key={schedulingStartDateTime ? schedulingStartDateTime.toISOString() : undefined}
            component={endDateField(t, props, disabled)}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <h2>{t('schedule.availableSeats')}</h2>
      </Row>
      <Row>
        <Col xs="6">
          <Field
            name="availableSeats"
            validate={
              disabledAvailableSeats ? undefined : composeValidators(isInteger, inRange(0, Number.POSITIVE_INFINITY))
            }
            render={availableSeatsField(t, disabledAvailableSeats)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Infobox>{t('schedule.numberOfSeatsMessage')}</Infobox>
        </Col>
      </Row>
    </div>
  );
};

export class CreateScheduleSceneComponent extends PureComponent<CreateScheduleSceneProps, CreateScheduleSceneState> {
  private formInitialValues?: FormValues;

  public constructor(props: CreateScheduleSceneProps) {
    super(props);

    const {
      location: { state },
      match: {
        params: { action },
      },
    } = this.props;

    this.state = {
      oldFormValues: state ? state.oldFormValues : undefined,
      scheduleState: {
        allAgentsAreActive: true,
        isDirty: false,
        ...(state ? state.scheduleState : undefined),
      },
      isEdit: action === CreateEditView.EDIT,
      isView: action === CreateEditView.VIEW,
      isCreate: action === CreateEditView.CREATE,
      inactiveAgentModal: { open: false },
      dateRangeChangeModalStatus: 'ready',
    };

    this.formInitialValues = this.getFormInitialValues(this.props.initialScheduleInfo, this.state.oldFormValues);
  }

  public getFormInitialValues = (initialScheduleInfo?: ScheduleInfo, oldFormValues?: FormValues) => {
    let values: FormValues = {
      name: '',
      queueIds: [],
      availableSeats: '',
      cloneScheduleId: undefined,
      schedulingStartDateTime: undefined,
      schedulingEndDateTime: undefined,
      timezone: undefined,
    };

    let dateRangeValues: {
      schedulingStartDateTime: FormValues['schedulingStartDateTime'];
      schedulingEndDateTime: FormValues['schedulingEndDateTime'];
    } = {
      schedulingStartDateTime: undefined,
      schedulingEndDateTime: undefined,
    };

    const { schedulingStartDateTime, schedulingEndDateTime } = oldFormValues ?? {};

    if (initialScheduleInfo) {
      const { availableSeats, scheduleRange, timezone, ...restScheduleInfo } = initialScheduleInfo;
      values = {
        ...restScheduleInfo,
        timezone,
        availableSeats: isNil(availableSeats) ? '' : String(availableSeats),
      };
      dateRangeValues = {
        schedulingStartDateTime: utcToZonedTime(new Date(scheduleRange.startDateTime), timezone),
        schedulingEndDateTime: convertFieldsEndOfDateRange(
          utcToZonedTime(new Date(scheduleRange.endDateTime), timezone)
        ),
      };
    } else {
      dateRangeValues = {
        schedulingStartDateTime,
        schedulingEndDateTime,
      };
    }

    return {
      ...values,
      ...oldFormValues,
      ...dateRangeValues, // values need to overwrite schedulingStartDateTime and schedulingEndDateTime from oldFormValues
    };
  };

  public render() {
    const { createSchedule, updateSchedule, updateScheduleInfo } = this.props;

    return (
      <Form
        keepDirtyOnReinitialize
        onSubmit={this.handleSubmitForm(createSchedule, updateSchedule, updateScheduleInfo)}
        initialValues={this.formInitialValues}
        render={this.renderForm}
      />
    );
  }

  protected postCreationCallback = (formValues: FormValues) => {
    const { history, location } = this.props;
    const { isEdit, createdScheduleId } = this.state;

    if (!isEdit && createdScheduleId) {
      this.setState(
        ({ scheduleState }) => ({
          scheduleState: {
            ...scheduleState,
            isDirty: false,
          },
          isCreate: false,
          isEdit: true,
        }),
        () =>
          history.replace(getPathname(CreateEditView.EDIT, createdScheduleId), {
            ...location.state,
            oldFormValues: formValues,
          })
      );
    }
  };

  protected renderForm = (props: FormRenderProps<FormValues>) => {
    const { t, queuesResult, schedulesResult, createSchedule, updateSchedule, updateScheduleInfo } = this.props;
    const {
      isEdit,
      isView,
      isCreate,
      inactiveAgentModal,
      dateRangeChangeModalStatus,
      oldFormValues,
      scheduleState: { allAgentsAreActive, isDirty, schedule },
    } = this.state;
    const scheduleName = schedule ? schedule.info.name : props.values.name;

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      if (allAgentsAreActive) {
        try {
          await props.handleSubmit(event);
          this.postCreationCallback(props.values);
        } catch (e) {
          // `customFetch()` will display error message
        }
      } else {
        event.preventDefault();
        this.setState({
          inactiveAgentModal: {
            open: true,
            onSubmitSuccess: () => {
              this.postCreationCallback(props.values);
              this.setState({ inactiveAgentModal: { open: false } });
            },
            onSubmitError: () => null,
          },
        });
      }
    };

    return (
      <ReactstrapForm onSubmit={onSubmit}>
        <FormSpy<FormValues>
          subscription={{ dirty: true, modified: true, valid: true, values: true }}
          onChange={(onChangeProps) => {
            const { modified } = onChangeProps;
            // Do not fire on initial render, when updating state is causing React error
            if (modified && Object.values(modified).filter((filteredValueModified) => filteredValueModified).length) {
              this.formOnChange(onChangeProps);
            }
          }}
        />
        <OnChange name={nameOf<FormValues>('schedulingStartDateTime')}>
          {(value) => this.formOnDateRangeChange('schedulingStartDateTime', value)}
        </OnChange>
        <OnChange name={nameOf<FormValues>('schedulingEndDateTime')}>
          {(value) => this.formOnDateRangeChange('schedulingEndDateTime', value)}
        </OnChange>
        <PromptModal
          t={t}
          when={isDirty}
          onSave={async ({ proceed, close }) => {
            if (allAgentsAreActive) {
              try {
                await props.handleSubmit();
                proceed();
              } catch (e) {
                close();
              }
            } else {
              this.setState({ inactiveAgentModal: { open: true, onSubmitSuccess: proceed, onSubmitError: close } });
            }
          }}
        />
        <Content header={this.headerProps(t, props)}>
          <CreateScheduleBody
            queuesResult={queuesResult}
            schedulesResult={schedulesResult}
            t={t}
            props={props}
            isView={isView}
            isEdit={isEdit}
            isCreate={isCreate}
            scheduleState={this.props.initialScheduleInfo?.state ?? this.state.scheduleState.schedule?.info.state}
          />
          {inactiveAgentModal.open && (
            <InactiveAgentConfirmationModal
              isOpen
              isEdit={isEdit}
              isMultiQueueSchedule={(props.form.getFieldState('queueIds')?.length ?? 0) > 1}
              scheduleName={scheduleName!}
              onSubmit={async () => {
                try {
                  await this.handleSubmitForm(
                    createSchedule,
                    updateSchedule,
                    updateScheduleInfo
                  )(props.values, props.form);

                  inactiveAgentModal.onSubmitSuccess();
                } catch (e) {
                  this.setState({ inactiveAgentModal: { open: false } });

                  inactiveAgentModal.onSubmitError();
                }
              }}
              onClose={() => this.setState({ inactiveAgentModal: { open: false } })}
              t={t}
            />
          )}
          {dateRangeChangeModalStatus === 'open' && (
            <DateRangeChangeModal
              isOpen
              onSubmit={() => {
                // Proceed with new date range, schedule data could be amended on save or next step
                this.setState({
                  dateRangeChangeModalStatus: 'obsolete',
                });
              }}
              onClose={() =>
                this.setState(
                  {
                    dateRangeChangeModalStatus: 'ready',
                  },
                  () => {
                    // Return back old date range values, schedule data won't be amended
                    if (!!oldFormValues) {
                      props.form.change('schedulingStartDateTime', oldFormValues.schedulingStartDateTime);
                      props.form.change('schedulingEndDateTime', oldFormValues.schedulingEndDateTime);
                    }
                  }
                )
              }
              t={t}
            />
          )}
        </Content>
      </ReactstrapForm>
    );
  };

  protected getTitleKey = () => {
    const { isEdit, isView } = this.state;

    if (isEdit) return 'schedule.edit';
    if (isView) return 'schedule.view';

    return 'schedule.create';
  };

  protected headerProps = (t: TFunction, formRenderProps: FormRenderProps<FormValues>) => {
    const { action, id } = this.props.match.params;

    return {
      actionBar: (
        <Row>
          <Col sm="auto">
            <Button
              data-test-id={getButtonTestId(action, 1, NEXT_STEP_BUTTON)}
              color="primary"
              kind="icon"
              disabled={formRenderProps.invalid}
              tag={Link}
              title={t('layout.steps.next')}
              to={getComposerPathname(action, id)}
              onClick={(e: any) => {
                e.preventDefault();
                this.goToSecondStep(formRenderProps.values);
              }}
            >
              <ArrowRightIcon className="text-white" />
            </Button>
          </Col>
          <Separator />
          {!this.state.isView && (
            <Col sm="auto">
              <Button
                data-test-id={getButtonTestId(action, 1, SAVE_BUTTON)}
                color="secondary"
                type="submit"
                disabled={formRenderProps.invalid}
              >
                {t('layout.action.save')}
              </Button>
            </Col>
          )}
          <Col sm="auto">
            <Button
              data-test-id={getButtonTestId(action, 1, CANCEL_BUTTON)}
              className="button-icon-left"
              color="info"
              icon={{ color: 'red', kind: 'cross', size: 10 }}
              tag={Link}
              to={PATH_SCHEDULING_STUDIO}
            >
              {t('layout.action.cancel')}
            </Button>
          </Col>
        </Row>
      ),
      testId: CREATE_TITLE_TAG,
      title: t(this.getTitleKey()),
      titleSlug: `${action}-schedule`,
    };
  };

  protected handleSubmitForm =
    (
      createSchedule: CreateScheduleMutationFn,
      updateSchedule: UpdateScheduleMutationFn,
      updateScheduleInfo: UpdateScheduleInfoMutationFn
    ) =>
    async (values: FormValues, form: FormApi) => {
      const { id } = this.props.match.params;
      const {
        isCreate,
        isEdit,
        scheduleState: { isDirty, scheduledAgents: composerScheduledAgents = [] },
        oldFormValues,
        dateRangeChangeModalStatus,
      } = this.state;

      let {
        scheduleState: { usedScheduleElements = [], scheduleDaysSettings },
      } = this.state;

      // Need to "un-dirty" the state to avoid redirect blocking
      this.setState(({ scheduleState }) => ({
        scheduleState: {
          ...scheduleState,
          isDirty: false,
        },
      }));

      const {
        queueIds,
        schedulingEndDateTime,
        schedulingStartDateTime,
        name,
        cloneScheduleId,
        availableSeats,
        timezone,
      } = values;

      let elements: ScheduleItemInput[] = [];
      let agents: ScheduledAgentInput[] = [];
      let settings: ScheduleDaySettingsInput[] = [];

      if (isCreate && cloneSchedule(values, oldFormValues)) {
        const templateSchedule = await this.props.client.query<GetScheduleQuery>({
          query: GetScheduleDocument,
          variables: { id: cloneScheduleId },
        });

        const {
          data: { schedule },
        } = templateSchedule;

        if (schedule) {
          const { scheduledAgents, scheduleItems } = schedule;

          agents = scheduledAgents;
          elements = scheduleItems
            ? shiftItemsDateRange(scheduleItems, schedulingStartDateTime!, schedulingEndDateTime!)
            : [];
          settings = getDefaultDaySettings(schedulingStartDateTime!, schedulingEndDateTime!, scheduledAgents);
        }
      } else {
        agents = composerScheduledAgents;

        // Update schedule data based on changed date range
        if (dateRangeChangeModalStatus === 'obsolete') {
          const { elements: updatedElements, settings: updatedSettings } = this.getUpdatedScheduleData(
            schedulingStartDateTime!,
            schedulingEndDateTime!
          );

          usedScheduleElements = updatedElements;
          scheduleDaysSettings = updatedSettings;
        }

        elements = usedScheduleElements.map((usedScheduleElement) => ({
          agentId: usedScheduleElement.agentId,
          dateTimeRange: {
            endDateTime: new Date(usedScheduleElement.end).toISOString(),
            startDateTime: new Date(usedScheduleElement.start).toISOString(),
          },
          pinned: usedScheduleElement.pinned,
          scheduleElementId: usedScheduleElement.scheduleElementId,
        }));

        settings = scheduleDaysSettings || getDefaultDaySettings(schedulingStartDateTime!, schedulingEndDateTime!);
      }

      const variables: CreateScheduleMutationVariables = {
        allowInactiveAgents: true,
        schedule: {
          scheduledAgents: agents,
          info: {
            availableSeats: availableSeats ? Number(availableSeats) : undefined,
            name: name!,
            queueIds: queueIds!,
            author: 'Guest', // TODO: provide proper user identifier
            notes: '',
            scheduleRange: {
              startDateTime: zonedTimeToUtc(schedulingStartDateTime!, timezone!).toISOString(),
              endDateTime: zonedTimeToUtc(createEndOfDateRange(schedulingEndDateTime!), timezone!).toISOString(),
            },
            timezone: timezone!,
          },
          scheduleItems: elements,
          daysSettings: settings,
        },
      };

      try {
        if (isCreate) {
          const { data: resultData } = await createSchedule({
            variables,
            awaitRefetchQueries: true,
            refetchQueries: [
              {
                query: GetSchedulesDocument,
                variables: defaultGetSchedulesVariables,
              },
            ],
          });

          if (resultData?.createSchedule?.id) {
            await new Promise((resolve) => {
              // wait until new schedule's id is stored in state, so that we could continue with 'postCreationCallback'
              this.setState(
                {
                  createdScheduleId: resultData!.createSchedule!.id,
                },
                () => resolve(true)
              );
            });
          }
        }

        if (isEdit && id) {
          if (elements.length === 0) {
            await updateScheduleInfo({
              variables: { id, name, availableSeats: availableSeats ?? '' },
            });
          } else {
            await updateSchedule({ variables: { ...variables, id } });
          }
        }

        // Re-initialize form to have proper dirty state on subsequent form changes
        form.initialize(values);
      } catch (error) {
        // error message provided by response provider
        this.setState(({ scheduleState }) => ({ scheduleState: { ...scheduleState, isDirty } }));

        throw error;
      }
    };

  protected goToSecondStep = async (formValues: FormValues) => {
    const {
      match: {
        params: { action, id },
      },
    } = this.props;
    const forceRefetchSchedule = this.props?.location?.state?.forceRefetchSchedule;

    const { oldFormValues, scheduleState, dateRangeChangeModalStatus } = this.state;
    const { cloneScheduleId, schedulingStartDateTime, schedulingEndDateTime, timezone } = formValues;

    let { usedScheduleElements, scheduleDaysSettings, autoScheduleSelectedRange } = scheduleState;

    // Update schedule data based on changed date range
    if (dateRangeChangeModalStatus === 'obsolete') {
      const { elements, settings } = this.getUpdatedScheduleData(schedulingStartDateTime!, schedulingEndDateTime!);

      usedScheduleElements = elements;
      scheduleDaysSettings = settings;
      autoScheduleSelectedRange = undefined; // undefined value will trigger that autoSchedule selected range will be set on scheduleComposer initialization
    }

    this.setState(
      (state) => ({
        scheduleState: {
          ...state.scheduleState,
          usedScheduleElements,
          scheduleDaysSettings,
          isDirty: false,
        },
      }),
      () => {
        this.props.history.push({
          state: {
            formValues: {
              ...formValues,
              schedulingStartDateTime: schedulingStartDateTime && zonedTimeToUtc(schedulingStartDateTime, timezone!),
              schedulingEndDateTime: schedulingEndDateTime && zonedTimeToUtc(schedulingEndDateTime, timezone!),
            },
            oldFormValues,
            scheduleState: {
              ...scheduleState,
              usedScheduleElements,
              scheduleDaysSettings,
              autoScheduleSelectedRange,
              ...(cloneScheduleId && {
                usePrecalculatedQueuesStaffing: false,
              }),
            },
            forceRefetchSchedule,
          },
          pathname: getComposerPathname(action, id),
        });
      }
    );
  };

  // Create schedule data based on new date range for selected agents and used elements
  protected getUpdatedScheduleData = (startDateTime: Date, endDateTime: Date) => {
    const { usedScheduleElements = [], scheduledAgents, scheduleDaysSettings = [] } = this.state.scheduleState;
    const { schedulingStartDateTime: oldStartDateTime } = this.state.oldFormValues!;
    const extendedEndDateRange = createEndOfDateRange(endDateTime);
    const endOfDateRange = createEndOfDateRange(extendedEndDateRange);

    // Remove elements outside new date range
    const elements = usedScheduleElements.filter((element) => {
      // element shouldn't start at the end of the range
      const isStartWithinInterval = isWithinInterval(element.start, {
        start: startDateTime.getTime(),
        end: endOfDateRange.getTime() - 1,
      });

      // element shouldn't end at the start of the range
      const isEndWithinInterval = isWithinInterval(element.end, {
        start: startDateTime.getTime() + 1,
        end: endOfDateRange.getTime(),
      });

      if (isStartWithinInterval && isEndWithinInterval) {
        // both dates are within interval - keep element
        return true;
      }

      if (!isStartWithinInterval && !isEndWithinInterval) {
        // both dates are not within interval
        if (isAfter(element.end, endOfDateRange.getTime()) && isBefore(element.start, startDateTime.getTime())) {
          // in theory scheduled element can be larger than schedule interval, so crop from both sides
          element.start = startDateTime.getTime();
          element.end = endOfDateRange.getTime();
          return true;
        }

        // otherwise, remove element
        return false;
      }

      if (!isStartWithinInterval) {
        // start is not within this interval, but end is -> set element start to start of entire interval
        element.start = startDateTime.getTime();
        return true;
      }

      if (!isEndWithinInterval) {
        // end is not within this interval, but start is -> set element end to end of entire interval
        element.end = endOfDateRange.getTime();
        return true;
      }
    });

    let settings = getDefaultDaySettings(startDateTime, endDateTime, scheduledAgents);

    // Shift and merge old day settings
    const diff = differenceInCalendarDays(startDateTime, oldStartDateTime!);

    settings = settings.map((day, dayIndex) => {
      const setting = scheduleDaysSettings[dayIndex + diff] || day;

      return {
        ...setting,
        dayIndex: day.dayIndex,
      };
    });

    return {
      elements,
      settings,
    };
  };

  private formOnChange = (props: FormState<FormValues>) => {
    this.setState(({ scheduleState, scheduleState: { isDirty }, isCreate, isEdit }) => ({
      scheduleState: {
        ...scheduleState,
        isDirty: (isCreate && props.valid) || (isEdit && (props.dirty || isDirty)),
      },
    }));
  };

  private formOnDateRangeChange = <
    T extends keyof Pick<FormValues, 'schedulingStartDateTime' | 'schedulingEndDateTime'>
  >(
    name: T,
    value: FormValues[T]
  ) => {
    const { dateRangeChangeModalStatus, oldFormValues } = this.state;

    if (dateRangeChangeModalStatus !== 'ready') {
      return;
    }

    if (!!oldFormValues && !isEqual(value!, oldFormValues[name]!)) {
      this.setState({ dateRangeChangeModalStatus: 'open' });
    }
  };
}

export default withRouter(withTranslation()(CreateScheduleSceneComponent));
